/**
 * Display formatting for numbers, units and labels.
 *
 * These lived in validation.ts, which meant one file owned two unrelated
 * concerns: deciding whether input is valid, and rendering values for the
 * screen. Nothing here validates anything.
 */

import { TransactionDiscount } from "types/financial";

// Format a single label value
export const formatLabel = (value: any): string => {
  if (value === null || value === undefined) return "";
  const str = String(value).replace(/_/g, " ");
  if (!str.length) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Format comma-separated values or a single value
export const formatLabels = (value: any): string => {
  if (value === null || value === undefined) return "";
  const str = String(value);

  if (str.includes(",")) {
    return str
      .split(",")
      .map((item) => formatLabel(item.trim()))
      .join(", ");
  }

  return formatLabel(str);
};

// Joins a formatted number to its unit. Units are spaced ("80.5 kg") except
// %, which is not ("1.23%"). Keep every unit-bearing value going through this
// so the same field cannot render two ways in two places.
export const withUnit = (formatted: string, unit?: string): string => {
  if (!unit) return formatted;
  return `${formatted}${unit === "%" ? "" : " "}${unit}`;
};

// Every numeric formatter accepts the API's mix of numbers and decimal-cast
// strings; null when there is nothing usable to format.
const parseNumeric = (
  num: number | string | null | undefined
): number | null => {
  if (num === null || num === undefined || num === "") return null;
  const parsed = typeof num === "string" ? parseFloat(num) : num;
  return isNaN(parsed) ? null : parsed;
};

const formatLocale = (
  num: number | string | null | undefined,
  options?: Intl.NumberFormatOptions
): string => {
  const parsed = parseNumeric(num);
  return parsed === null ? "" : parsed.toLocaleString("en-US", options);
};

// Format number with American style
export const formatNumber = (num: number | string | null | undefined): string =>
  formatLocale(num);

// One decimal place - the precision the mobile app shows for check-in and
// measurement values.
export const formatNumberOneDecimal = (
  num: number | string | null | undefined
): string =>
  formatLocale(num, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

// Remove unnecessary decimals
export const formatNumberClean = (
  num: number | string | null | undefined
): string =>
  formatLocale(num, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Placeholder for a value the API did not supply.
export const EMPTY_VALUE = "—";

// The one display rule for measured quantities (weights, grams, kcal, mg):
// one decimal, unit attached, EMPTY_VALUE when the API supplied nothing.
export const formatOneDecimalWithUnit = (
  num: number | string | null | undefined,
  unit?: string
): string => {
  const formatted = formatNumberOneDecimal(num);
  return formatted ? withUnit(formatted, unit) : EMPTY_VALUE;
};

// Same rule for a delta: explicit + on gains ("+1.2 kg", "-250.0 kcal").
export const formatSignedOneDecimalWithUnit = (
  num: number | string | null | undefined,
  unit?: string
): string => {
  const formatted = formatNumberOneDecimal(num);
  if (!formatted) return EMPTY_VALUE;
  const positive = (parseNumeric(num) as number) > 0;
  return withUnit(`${positive ? "+" : ""}${formatted}`, unit);
};

// Signed percentage ("+1.2%", "-0.8%"); null when the API supplied no usable
// value so callers can render a placeholder in their own style.
export const formatSignedPercent = (
  raw: number | string | null | undefined
): string | null =>
  parseNumeric(raw) === null ? null : formatSignedOneDecimalWithUnit(raw, "%");

// Text colour by numeric sign: gains green, losses red. Accepts raw numbers,
// decimal-cast strings and already-formatted deltas ("-0.4 kg"). Zero and
// unparseable values get `neutral` - empty by default so the parent's colour
// shows through.
export const signTone = (
  value: number | string | null | undefined,
  neutral = ""
): string => {
  const parsed = parseNumeric(value);
  if (parsed === null || parsed === 0) return neutral;
  return parsed > 0
    ? "text-green-600 dark:text-green-400"
    : "text-red-600 dark:text-red-400";
};

// "$9.74 USD" - the financial views' money format. The API sends major units.
// Null rather than "$0.00" when there is no usable amount.
export const formatMoney = (
  amount: number | string | null | undefined,
  currency?: string | null
): string | null => {
  const parsed = parseNumeric(amount);
  if (parsed === null) return null;
  return `$${parsed.toFixed(2)}${currency ? ` ${currency.toUpperCase()}` : ""}`;
};

// Why a Stripe charge cost less than the plan price. Worded identically on the
// website and in the mobile app; null at full price.
export const formatDiscountReason = (
  discount?: TransactionDiscount | null
): string | null => {
  if (!discount) return null;

  switch (discount.reason) {
    case "promotion_code":
      return discount.code ? `Promo code ${discount.code}` : "Promo code";
    case "coupon":
    default:
      return discount.coupon_name
        ? `Discount · ${discount.coupon_name}`
        : "Discount";
  }
};

// The line under a discounted amount: the list price it came down from, and why.
export const formatDiscountSubline = (
  discount?: TransactionDiscount | null
): string | null => {
  const reason = formatDiscountReason(discount);
  if (!reason) return null;
  const original = formatMoney(discount.original_amount);
  return original ? `was ${original} · ${reason}` : reason;
};

// The saving itself, e.g. "−$3.25 USD (25% off)". Null at full price.
export const formatDiscountAmount = (
  discount?: TransactionDiscount | null
): string | null => {
  const saved = formatMoney(discount?.discount_amount, discount?.currency);
  if (!saved) return null;
  return discount.percent_off
    ? `−${saved} (${discount.percent_off}% off)`
    : `−${saved}`;
};

export const formatProductName = (productName: string): string =>
  productName.replace(/[_-]nutrition(?=[_-]|$)/g, "");

// A subscription's cadence from the API's interval fields: "Monthly", "Yearly" or
// "Every 6 months". EMPTY_VALUE when the API gave no interval.
export const formatBillingPeriod = (
  interval?: string | null,
  intervalCount?: number | null
): string => {
  if (!interval) return EMPTY_VALUE;
  const unit = interval.toLowerCase();
  const count = intervalCount ?? 1;

  if (count > 1) return `Every ${count} ${unit}s`;
  if (unit === "month") return "Monthly";
  if (unit === "year") return "Yearly";
  return formatLabel(unit);
};

// Avatar fallback: first letter of each name, upper-cased. Missing names
// contribute nothing, so a single-name user gets one letter.
export const getInitials = (
  firstName?: string | null,
  lastName?: string | null
): string =>
  `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
